/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * videoDebugOverlayStorage — persistence for the QA "keep the video debug
 * overlay on" preference.
 *
 * ## Why `localStorage` and not IndexedDB
 *
 * `StorageGuidelines.md` §2 routes all *application data* through the
 * IndexedDB adapter. This is not application data: it is a single boolean
 * describing how a QA engineer wants their own device to behave, it must
 * be readable synchronously while the player screen is mounting (an async
 * read would let the overlay flash in or out after first paint), and
 * losing it costs nothing. That is the same exemption `appVersionOverride`
 * and `mockOverride` already take, which is why this is a flat file in
 * `src/devtools/` beside them rather than anything under `src/model/`.
 *
 * ## Why this one is NOT gated on `isDevToolsBuild()`
 *
 * Unlike its neighbours, every entry point here works in a release build.
 * The overlay ships in all builds by design — the proposal's field-support
 * case is diagnosing a real customer's playback without first getting a
 * debug build onto their device, and a release-inert preference would make
 * that impossible. The overlay has no privileged mode either: every metric
 * is shown in every build.
 *
 * Storing nothing but `"true"`/`"false"` under one key means a hand-edited
 * or corrupt value can only ever fail closed, to hidden.
 */

import { Log } from "../model/logging/Log";

const TAG: string = "videoDebugOverlayStorage";

/** Namespaced key holding the persisted overlay preference. */
export const VIDEO_DEBUG_OVERLAY_STORAGE_KEY: string =
  "videoDebugOverlay.enabled";

function hasBrowserStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

/**
 * Whether the overlay has been left switched on for this device.
 *
 * Anything other than the literal string `"true"` reads as off, so a
 * missing key, an unreadable store and a hand-edited value all fail closed.
 *
 * @returns True only when the preference was explicitly persisted as on.
 */
export function readVideoDebugOverlayEnabled(): boolean {
  if (!hasBrowserStorage()) {
    return false;
  }
  try {
    return (
      window.localStorage.getItem(VIDEO_DEBUG_OVERLAY_STORAGE_KEY) === "true"
    );
  } catch (error: unknown) {
    Log.w(TAG, "readVideoDebugOverlayEnabled() — unreadable store", error);
    return false;
  }
}

/**
 * Persist the overlay preference so it survives a reload or app restart.
 *
 * A storage failure is logged and swallowed: the in-memory toggle has
 * already taken effect for this session, and failing the toggle because
 * the preference could not be written would be a worse outcome than a
 * preference that does not survive the next launch.
 *
 * @param isEnabled - True to keep the overlay on across restarts.
 */
export function writeVideoDebugOverlayEnabled(isEnabled: boolean): void {
  if (!hasBrowserStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(
      VIDEO_DEBUG_OVERLAY_STORAGE_KEY,
      isEnabled ? "true" : "false"
    );
  } catch (error: unknown) {
    Log.w(TAG, "writeVideoDebugOverlayEnabled() — could not persist", error);
  }
}
